import React, { useState, useEffect } from 'react';
import Logger from '../../../../shared/Logger';
import {
  Button,
  Table,
  Flex,
  theme,
  Panel,
  Box,
  Text,
} from '@xometry/xometry_loft';
import usersApi from '../../../../services/users';
import {
  OrganizationDefault,
  OrganizationQuotePreset,
  QuotePatchRequirement,
} from '../../../../schema/generated/schema';
import {
  isQuotePresetPatchEmpty,
  QUOTE_PRESET_STANDARD_INSPECTION_LABEL,
} from './quotePresetPatch';

type PresetListProps = {
  teamInfo: OrganizationDefault;
  isStaff: boolean;
  toggleEditView: (
    presetId: string,
    patchRequirement: QuotePatchRequirement
  ) => void;
  hasWriteOrgPermission: boolean;
};

type ReadablePresetItem = {
  id: string | null;
  label: string;
  value: string;
};

enum QuotePresetLabel {
  // TODO: add more labels as we need it
  INSPECTION = 'Inspection',
  CERTIFICATES = 'Certificates and Supplier Qualifications',
  PROCESS = 'Process',
}

type ReadablePresetData = {
  updatedDate: string;
  process: string | undefined;
  updatedBy: string;
  presetId: string;
  patchRequirement: OrganizationQuotePreset['patchRequirement'];
  invalidRequirement: string | null;
  inspection: ReadablePresetItem | undefined;
  certs: string;
};

function processLabelFromPresetInfo(
  presetInfo: ReadablePresetItem[],
  menuId: string
): string | undefined {
  const fromApi = presetInfo.find(
    (item: ReadablePresetItem) => item.label === QuotePresetLabel.PROCESS
  )?.value;
  if (fromApi && fromApi.trim() !== '') {
    return fromApi;
  }
  return menuId;
}

const PresetsList = (Props: PresetListProps) => {
  const {
    teamInfo,
    isStaff = false,
    toggleEditView,
    hasWriteOrgPermission,
  } = Props;
  const [readablePresetData, setReadablePresetData] = useState<
    ReadablePresetData[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const teamPresets = teamInfo?.quotePresets;

  const getReadablePreset = async (
    preset: OrganizationQuotePreset
  ): Promise<ReadablePresetItem[]> => {
    if (!preset.patchRequirement) {
      return [];
    }

    const payload = {
      allowStaffFeatures: isStaff,
      patch: preset.patchRequirement,
    };

    try {
      const res = await usersApi.postPatchRequirementsInfo(payload);
      return res?.data?.display ?? [];
    } catch (e) {
      Logger.error('Error fetching preset data', e, {
        component: 'PresetsList',
        action: 'fetchPresetData',
      });
      return [];
    }
  };

  const transformPresetData = async (
    preset: OrganizationQuotePreset
  ): Promise<ReadablePresetData> => {
    const presetInfo = await getReadablePreset(preset);
    const patchEmpty = isQuotePresetPatchEmpty(preset.patchRequirement);

    if (patchEmpty) {
      return {
        updatedDate: new Date(preset.updatedAt).toLocaleDateString('en-US'),
        process: processLabelFromPresetInfo(presetInfo, preset.menuId),
        updatedBy: formatUpdatedBy(preset),
        inspection: {
          id: null,
          label: QuotePresetLabel.INSPECTION,
          value: QUOTE_PRESET_STANDARD_INSPECTION_LABEL,
        },
        certs: '',
        presetId: preset.id,
        patchRequirement: preset.patchRequirement,
        invalidRequirement: preset.invalidRequirement,
      };
    }

    return {
      updatedDate: new Date(preset.updatedAt).toLocaleDateString('en-US'),
      process: presetInfo.find(
        (item: ReadablePresetItem) => item.label === QuotePresetLabel.PROCESS
      )?.value,
      updatedBy: formatUpdatedBy(preset),
      inspection: presetInfo.find(
        (item: ReadablePresetItem) => item.label === QuotePresetLabel.INSPECTION
      ),
      certs: presetInfo
        .filter(
          (item: ReadablePresetItem) =>
            item.label === QuotePresetLabel.CERTIFICATES &&
            item.value !== 'None'
        )
        .map((item: ReadablePresetItem) => item.value)
        .join(', '),
      presetId: preset.id,
      patchRequirement: preset.patchRequirement,
      invalidRequirement: preset.invalidRequirement,
    };
  };

  const fetchReadablePresets = async () => {
    if (!teamPresets) return;

    setLoading(true);
    setError(null);
    try {
      const readableData = await Promise.all(
        teamPresets.map(transformPresetData)
      );
      setReadablePresetData(readableData);
    } catch (e) {
      setError('Failed to load presets.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (teamPresets) {
      fetchReadablePresets();
    }
  }, [teamPresets]);

  if (!teamPresets) return null;
  if (loading) return <Flex>Loading presets...</Flex>;
  if (error) return <p>{error}</p>;
  return (
    <Panel kind={theme.kind.primary} flexDirection="column">
      <Table margin={'8px 20px 20px'} width="fill-available">
        <thead>
          <tr>
            <th>Process</th>
            <th>Presets</th>
            <th width="25%">Updated By</th>
            <th>Updated On</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {readablePresetData && readablePresetData.length > 0 ? (
            readablePresetData.map((data: ReadablePresetData) => {
              const {
                inspection,
                certs,
                updatedDate,
                updatedBy,
                process,
                presetId,
                patchRequirement,
                invalidRequirement,
              } = data;
              return (
                <tr key={presetId}>
                  <td>{process}</td>
                  <td
                    style={{
                      backgroundColor: invalidRequirement
                        ? theme.colors.yellows[10]
                        : theme.colors.white,
                    }}
                  >
                    {inspection && (
                      <Box>
                        <Text fontWeight="600">{`Inspection: `}</Text>
                        {inspection.value}
                      </Box>
                    )}
                    {certs && (
                      <Box>
                        <Text fontWeight="600">{`Certificates and Supplier Qualifications: `}</Text>
                        {certs}
                      </Box>
                    )}
                  </td>
                  <td>{updatedBy}</td>
                  <td> {updatedDate}</td>
                  <td>
                    {hasWriteOrgPermission && (
                      <Button
                        kind="text-blue"
                        onClick={() =>
                          toggleEditView(
                            presetId,
                            patchRequirement as QuotePatchRequirement
                          )
                        }
                      >
                        Edit
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })
          ) : (
            <tr>
              <td
                colSpan={5}
                style={{
                  textAlign: 'center',
                }}
              >
                <Text
                  kind="body-sm"
                  style={{
                    color: theme.colors.darkBlues[75],
                  }}
                >
                  No quote presets yet.
                </Text>
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </Panel>
  );
};

function formatUpdatedBy(preset: OrganizationQuotePreset): string {
  const first = preset.updatedBy?.firstName?.trim() ?? '';
  const last = preset.updatedBy?.lastName?.trim() ?? '';
  const name = [first, last].filter(Boolean).join(' ');
  return name || '—';
}

export default PresetsList;
